import React from 'react';
import BreakdownTable from './BreakdownTable';

/**
 * 인쇄/PDF용 공식 견적서 문서 템플릿
 * 
 * @param {Object} props
 * @param {Array} props.breakdown - 품목 배열
 * @param {Object} props.totals - { totalExclVAT, totalInclVAT }
 * @param {Object} props.supplier - 공급사 정보
 * @param {Object} props.recipient - 수신자 정보 (폼에서 입력)
 * @param {Object} props.terms - 견적조건
 * @param {string} props.documentNumber - 견적번호
 * @param {Date} props.issueDate - 발행일
 */
const EstimateDocumentTemplate = ({
    breakdown = [],
    totals = { totalExclVAT: 0, totalInclVAT: 0 },
    supplier = {},
    recipient = {},
    terms = {},
    documentNumber = '',
    issueDate = new Date()
}) => {
    const formatDate = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
    };



    // A4 padding reduced to 10mm, base font reduced to 10pt
    const docStyle = {
        padding: '10mm',
        fontSize: '10pt',
        lineHeight: '1.4',
        fontFamily: "'Pretendard', sans-serif"
    };



    const headerStyle = {
        marginBottom: '15px',
        paddingBottom: '5px',
        borderBottom: '2px solid #333',
        textAlign: 'center'
    };

    const partyBoxStyle = {
        border: '1px solid #999',
        borderRadius: '4px',
        padding: '10px'
    };

    // Compact table cells
    const tableCellStyle = {
        padding: '2px 0',
        fontSize: '9pt'
    };

    return (
        <div className="estimate-document" style={docStyle}>
            {/* 문서 헤더 */}
            <header className="doc-header" style={headerStyle}>
                <h1 className="doc-title" style={{ fontSize: '20pt', letterSpacing: '0.2em', margin: 0 }}>예상 견적서</h1>
            </header>

            {/* 공급자 / 수신자 정보 (Compact Layout) */}
            <section className="parties" style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>

                {/* 수신자 (좌측 - 40%) */}
                <div className="party recipient" style={{ flex: '0 0 40%', ...partyBoxStyle }}>
                    <h4 className="party-title" style={{ margin: '0 0 8px 0', fontSize: '10pt', textAlign: 'center', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>수 신 자</h4>
                    <table className="party-info-table" style={{ width: '100%' }}>
                        <colgroup>
                            <col style={{ width: '60px' }} />
                            <col />
                        </colgroup>
                        <tbody>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>견적일자</th>
                                <td style={tableCellStyle}>{formatDate(issueDate)}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>상호</th>
                                <td style={tableCellStyle}>{recipient.companyName || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>담당자</th>
                                <td style={tableCellStyle}>{recipient.contactPerson || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>이메일</th>
                                <td style={tableCellStyle}>{recipient.email || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>연락처</th>
                                <td style={tableCellStyle}>{recipient.phone || '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* 공급자 (우측) */}
                <div className="party supplier" style={{ flex: '1', ...partyBoxStyle }}>
                    <h4 className="party-title" style={{ margin: '0 0 8px 0', fontSize: '10pt', textAlign: 'center', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>공 급 자</h4>
                    <table className="party-info-table" style={{ width: '100%' }}>
                        <colgroup>
                            <col style={{ width: '70px' }} />
                            <col />
                        </colgroup>
                        <tbody>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>상호</th>
                                <td style={tableCellStyle}>{supplier.name || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>대표자</th>
                                <td style={tableCellStyle}>{supplier.representative || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>사업자번호</th>
                                <td style={tableCellStyle}>{supplier.businessNumber || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>주소</th>
                                <td style={tableCellStyle}>{supplier.address || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>연락처</th>
                                <td style={tableCellStyle}>{supplier.phone || '-'}</td>
                            </tr>
                            <tr>
                                <th style={{ ...tableCellStyle, textAlign: 'left', color: '#555' }}>이메일</th>
                                <td style={tableCellStyle}>{supplier.email || '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* 품목 테이블 */}
            <section className="breakdown" style={{ marginBottom: '20px' }}>
                <BreakdownTable
                    breakdown={breakdown}
                    totals={totals}
                    mode="document"
                />
            </section>

            {/* 견적조건 */}
            <section className="terms" style={{ padding: '10px', background: '#f9f9f9', borderRadius: '4px', fontSize: '9pt' }}>
                <h4 className="terms-title" style={{ margin: '0 0 5px 0', fontSize: '10pt' }}>견적조건</h4>
                <ul className="terms-list" style={{ margin: 0, paddingLeft: '15px' }}>
                    <li style={{ marginBottom: '2px' }}>
                        <strong>주의:</strong> 본 견적은 예상견적입니다. 참고용으로만 사용해주세요. 정확한 견적은 문의 부탁드립니다.
                    </li>
                    <li style={{ marginBottom: '2px' }}>
                        <strong>납기:</strong> 범용 솔루션의 납기는 2~4주, 연동 및 개발 등이 포함된 경우 개발일정에 따라 변동됩니다.
                    </li>
                </ul>
            </section>
        </div>
    );
};

export default EstimateDocumentTemplate;
